import {dateFormat} from 'common/dateFormatterConfig';
import {AGREEMENT_TYPE_MAP} from 'models/agreements';
import {
    ELECTRICITY_AGREEMENT_STATUS_MAP,
    ElectricityAgreement,
    ElectricityAgreementStatus,
} from 'models/electricity/agreement';
import moment from 'moment';
import React, {useContext} from 'react';
import {Button, Spinner} from 'react-bootstrap';
import {AuthenticationContext, CustomerPermission} from 'services/context/authentication.context';
import {MeteringPointList} from './MeteringPoints/MeteringPointList';
import { getMarginal, getPackagePrice } from 'helpers/electricity-helper';
import { getPriceWithVAT, roundNumber } from 'helpers';
import { AgreementInfoColumn } from './AgreementInfoColumn';
import { ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP, ElectricityPackageBaseFixationType, ElectricityPackageEarlyTerminationFeeType } from 'models/electricity/new-electricity-agreement';
import { isNotNullOrUndefined } from 'common/data-util';
import { SUM_ROUNDING_SCALE } from 'common/constants';

interface ElectricityAgreementCardProps {
  agreement: ElectricityAgreement;
    changePackage: () => void;
    getFilePreview: () => void;
    isFilePreviewLoading: boolean;
    openOffer: () => void;
}

export default function ElectricityAgreementCard({ agreement, getFilePreview, isFilePreviewLoading, changePackage, openOffer }: ElectricityAgreementCardProps) {
    const { state, hasPermission } = useContext(AuthenticationContext);
    const isCompany = state.customer && !state.customer.isPerson && !state.customer.apartmentAssociation;
    const isVatLiable = isCompany && !!state.customer.vatRegistryNumber;

    const getAgreementStatusClassName = (status : ElectricityAgreementStatus) => {
        switch (status){
            case ElectricityAgreementStatus.ACTIVE:
                return 'active-status';
            case ElectricityAgreementStatus.ENDED:
                return 'ended-status';
            case ElectricityAgreementStatus.PENDING:
                return 'pending-status';
            case ElectricityAgreementStatus.REJECTED:
                return 'rejected-status';
            default:
                return 'active-status';
        }
    }

    const basePriceCentsKwh = agreement.basePriceCentsKwh ?? agreement.consumptionPackage.basePriceCentsKwh;
    const dayTariffCentsKwh = agreement.dayTariffCentsKwh ?? agreement.consumptionPackage.dayTariffCentsKwh;
    const nightTariffCentsKwh = agreement.nightTariffCentsKwh ?? agreement.consumptionPackage.nightTariffCentsKwh;

    const consumptionPackagePrice = agreement ? getPackagePrice(
        basePriceCentsKwh, 
        agreement.consumptionPackage.basePriceTextEt,
        agreement?.consumptionMarginal, 
        isCompany
    ) : '-';

    const consumptionPackageDayPrice = agreement.consumptionPackage.twoTariffPackage ? getPackagePrice(
        dayTariffCentsKwh, 
        undefined,
        agreement?.consumptionMarginal, 
        isCompany
    ) : '-';

    const consumptionPackageNightPrice = agreement.consumptionPackage.twoTariffPackage ? getPackagePrice(
        nightTariffCentsKwh, 
        undefined,
        agreement?.consumptionMarginal, 
        isCompany
    ) : '-';

    const hasAgreementInFuture = !!agreement ? 
        agreement.meteringPoints.some(mp => {
            if(!mp.relatedAgreement || !agreement.startDate)
                return false;
            if(mp.relatedAgreement.startDate == null){
                return true;
            }
            return moment(mp.relatedAgreement.startDate).isAfter(agreement.startDate);
        }) 
        : false;

    const ActionButtons = <div className="d-flex flex-column flex-lg-row w-100 mx-0">
        { agreement.status !== ElectricityAgreementStatus.PENDING ? (
            <div className="px-lg-2">
                <Button 
                    variant="outline-primary" 
                    size="lg" 
                    block 
                    className="d-flex align-items-center justify-content-center text-nowrap" 
                    disabled={isFilePreviewLoading} 
                    onClick={getFilePreview}
                >
                    {   isFilePreviewLoading &&
                        <Spinner animation="border" className="mr-2" />
                    }
                        Vaata lepingut
                </Button>
            </div>
            ) : null 
        }
        <div className="px-lg-2 mt-2 mt-lg-0">
            { 
            (agreement.status === ElectricityAgreementStatus.ACTIVE && !hasAgreementInFuture && hasPermission(CustomerPermission.WEB_ADMIN)) ? 
                <Button 
                variant="outline-primary" 
                size="lg" 
                className="text-nowrap" 
                block 
                onClick={changePackage}
                >
                    Vaheta paketti
                </Button> 
                : null
            }
            { agreement.status === ElectricityAgreementStatus.PENDING && hasPermission(CustomerPermission.WEB_ADMIN) ? (
                <Button variant="outline-primary" size="lg" className="text-nowrap" block onClick={openOffer}>Vaata pakkumist</Button> 
            ) : null
            }
        </div>
    </div>;

    return (
      <div className="d-flex flex-column bg-white rounded mt-1 mb-2 px-3 px-lg-4 py-5">
          <div className={`d-flex justify-content-between align-items-center ${!!agreement && "pb-2"} w-100`}>
              <div className="d-flex">
                  <i className={`icon__electricity d-inline-block mr-2 mt-1`}></i>
                  <div className="d-flex flex-column">
                      <div className="d-flex flex-column flex-lg-row align-items-lg-center">
                          <h2 className="u-fs-24 font-weight-bold mb-0">
                              {AGREEMENT_TYPE_MAP["ELECTRICITY"]} nr { agreement.contractNumber }
                          </h2>
                          <div className="electricity_agreements__status text-nowrap ml-lg-3 mt-lg-2 mt-4">
                              <span className={`${getAgreementStatusClassName(agreement.status)}`}>{ELECTRICITY_AGREEMENT_STATUS_MAP[agreement.status]}</span>
                          </div>
                      </div>
                      <div className="u-fs-20 mt-2 d-flex flex-column flex-lg-row">
                        <strong>{ agreement.consumptionPackage.nameEt }</strong>
                        {
                          agreement.consumptionPackage.greenEnergy && (
                            <div className="new-electricity-agreement__package-card__green-energy ml-lg-3">Roheenergia</div>
                          )
                        }
                        <div className="d-flex d-lg-none align-items-center">
                            {
                                agreement.consumptionPackage.twoTariffPackage ? (
                                    <div className='d-flex flex-column'>
                                        <div className='d-flex align-items-center mr-3'>
                                            <i className={'icon--16 icon__sun '} /><strong className='ml-2'>{consumptionPackageDayPrice}</strong>
                                        </div>
                                        <div className='d-flex align-items-center mr-3'>
                                            <i className={'icon--16 icon__moon '} /><strong className='ml-2'>{consumptionPackageNightPrice}</strong>
                                        </div>
                                    </div>
                                ) : (
                                    <strong className="mr-3">{consumptionPackagePrice}</strong>
                                )
                            }
                        </div>
                        <small className="d-flex d-lg-none u-fs-14 text-gray-700">
                            { isCompany ? `Lisandub käibemaks` : `Hinnad sisaldavad käibemaksu`}
                        </small>
                        </div>
                  </div>
              </div>
              <div className="d-flex align-items-center">
                  <div className="d-none d-lg-flex">
                      { ActionButtons }
                  </div>
              </div>
          </div>
          <CardDetailedInfo
            agreement={agreement}
            isCompany={isCompany}
            isVatLiable={isVatLiable}
            price={consumptionPackagePrice}
            dayPrice={consumptionPackageDayPrice}
            nightPrice={consumptionPackageNightPrice}
            actionButtons={ActionButtons}
          />
      </div>
    );
}

interface CardDetailedInfoProps {
  agreement: ElectricityAgreement;
  isCompany: boolean;
  isVatLiable: boolean;
  actionButtons: JSX.Element;
  price?: string;
  dayPrice?: string;
  nightPrice?: string;
}

function CardDetailedInfo({ agreement, isCompany, isVatLiable, price, dayPrice, nightPrice, actionButtons}: CardDetailedInfoProps) {
    const monthlyFeeEuros = (agreement.monthlyFeeEuros ?? agreement?.consumptionPackage.monthlyFeeEuros) ?? 0;

    const dynamicPackage = agreement.consumptionPackage.dynamicPackage;
    const baseConsumptionProportion = agreement.baseConsumptionProportion ?? agreement.consumptionPackage.baseConsumptionProportion;

    return (
      <>
          <div className="d-flex flex-column">
            <div className="electricity-agreement-card-detailed-info__table">
                <div className="d-flex flex-column flex-lg-row ml-3">
                      <AgreementInfoColumn
                          title="Lepingu algus"
                          value={agreement.startDate ? moment(agreement.startDate).format(dateFormat) : '-'}
                      />
                      <AgreementInfoColumn
                          title="Lepingu lõpp"
                          value={agreement.endDate ? moment(agreement.endDate).format(dateFormat) : agreement.startDate ? 'Tähtajatu' : '-'}
                      />
                  </div>
                  {
                    agreement.consumptionPackage.twoTariffPackage ? (
                        <>
                            <div className="d-none d-lg-flex flex-column flex-lg-row ml-3">
                                <AgreementInfoColumn
                                    title="Päevahind"
                                    value={dayPrice}
                                />
                                <AgreementInfoColumn 
                                    title='Ööhind:'
                                    value={nightPrice}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="d-none d-lg-flex flex-column flex-lg-row ml-3">
                            <AgreementInfoColumn
                                title={dynamicPackage ? "Fikseeritud tarbimise hind:" : "Hind:"}
                                value={price}
                            />
                        </div>
                    )
                  }
                  {
                    dynamicPackage && (
                        <div className="d-none d-lg-flex flex-column flex-lg-row ml-3">
                            <AgreementInfoColumn
                                title={"Fikseerimise tüüp"}
                                value={ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP[agreement.consumptionPackage.baseFixationType!]}
                            />
                            {
                                isNotNullOrUndefined(baseConsumptionProportion) && (
                                    <AgreementInfoColumn 
                                        title='Tarbimise osakaal:'
                                        value={`${roundNumber(
                                            baseConsumptionProportion, 
                                            getBaseConsumptionProportionScale(agreement.consumptionPackage.baseFixationType!
                                            ))
                                        } ${agreement.consumptionPackage.baseFixationType === ElectricityPackageBaseFixationType.PERCENTAGE ? '%' : 'MWh'}`}
                                    />
                                )
                            }
                        </div>
                    )
                  }
                  <div className="d-none d-lg-flex flex-column flex-lg-row ml-3">
                      <AgreementInfoColumn 
                            title='Kuutasu:'
                            value={`${isCompany ? roundNumber(monthlyFeeEuros , SUM_ROUNDING_SCALE) : getPriceWithVAT(monthlyFeeEuros, SUM_ROUNDING_SCALE, moment())} €`}
                        />
                      <AgreementInfoColumn />
                  </div>
                  <div className="d-none d-lg-flex flex-column flex-lg-row ml-3">
                      <AgreementInfoColumn
                          title={dynamicPackage ? "Börsihinnaga tarbimise marginaal:" : "Marginaal:"}
                          value={`${getMarginal(agreement.consumptionMarginal, isCompany)} s/kWh`}
                      />
                      <AgreementInfoColumn />
                  </div>
                  <small className="col d-none d-lg-block u-fs-14 text-gray-700 ml-3 mt-3">
                      { isCompany ? `Lisandub käibemaks` : `Hinnad sisaldavad käibemaksu`}
                  </small>
                 {(!!agreement?.productionMarginal) && (
                    <div className="d-flex flex-column">
                        <div className="u-fs-20 col d-flex flex-row flex-lg-row pt-3 pb-1">
                            <strong className="col">Tagasimüük</strong>
                        </div>
                        <div className="d-flex flex-column flex-lg-row ml-3 pt-1">
                            <AgreementInfoColumn
                                title="Marginaal:"
                                value={`${agreement.productionMarginal} s/kWh`}
                            />
                            <AgreementInfoColumn />
                        </div>
                        {isVatLiable && (
                            <small className="col d-none d-lg-block u-fs-14 text-gray-700 ml-3 mt-3">
                                Lisandub käibemaks
                            </small>
                        )}
                    </div>
                )}
                {
                agreement?.consumptionPackage.earlyTerminationFeeType === ElectricityPackageEarlyTerminationFeeType.PERCENTAGE_OF_VOLUME && (
                    <div className='ml-3'>
                        {
                            agreement.consumptionPackage.twoTariffPackage ? (
                                <>
                                    <p className="col mt-2 text-gray-700 u-fs-14">
                                        Ennetähtaegse lepingu lõpetamise või paketi vahetamise korral rakendub ennetähtaegse
                                        lõpetamise tasu. Tasu arvestatakse lepingu lõpuni jäänud perioodi kohta ning see 
                                        arvutatakse järgmiselt: <span className="text-dark font-weight-bold">{agreement.earlyTerminationFee}%</span> lepingu lõpuni jäänud arvestuslikust tarbimismahust, mis jaguneb päeva- ja öötarbimiseks, 
                                        korrutatuna vastavate lepingus kokkulepitud päeva- ja ööhindadega hinnangulise tarbimise alusel kogu lepingu perioodi kohta ning on:
                                    </p>
                                    <AgreementInfoColumn 
                                        title='Päevatarbimine:'
                                        value={`${agreement?.estimatedConsumptionDayVolumeMwh} MWh`}
                                    />
                                    <AgreementInfoColumn 
                                        title='Öötarbimine:'
                                        value={`${agreement?.estimatedConsumptionNightVolumeMwh} MWh`}
                                    />
                                    <AgreementInfoColumn 
                                        title='Kokku:'
                                        value={`${agreement?.estimatedConsumptionDayVolumeMwh! + agreement.estimatedConsumptionNightVolumeMwh!} MWh`}
                                    />
                                </>
                            ) : (
                                <p className="col mt-2 text-gray-700 u-fs-14">
                                    Ennetähtaegse lepingu lõpetamise või paketi vahetamise korral rakendub ennetähtaegse
                                    lõpetamise tasu. Tasu arvestatakse lepingu lõpuni jäänud perioodi kohta ning see
                                    arvutatakse järgmiselt: <span className="text-dark font-weight-bold">{agreement.earlyTerminationFee}%</span> lepingu lõpuni jäänud arvestuslik tarbimismaht korda lepingus
                                    kokkulepitud ühiku hind. Hinnanguline tarbimismaht lepingu perioodi kohta: <span className="text-dark font-weight-bold">{agreement.estimatedConsumptionVolumeMwh} MWh</span>.
                                </p>
                            )
                        }
                    </div>
                )
            }
            </div>
          </div>
          <div className="d-flex d-lg-none px-2 px-lg-4 my-3">
              { actionButtons }
          </div>
          <MeteringPointList
            meteringPoints={agreement?.meteringPoints}
            withProductionCapacity={!!agreement?.productionMarginal}
            isOffer={agreement?.status === ElectricityAgreementStatus.PENDING}
          />
      </>
    )
}

export const getBaseConsumptionProportionScale = (fixationType: ElectricityPackageBaseFixationType) => {
    return fixationType === ElectricityPackageBaseFixationType.PERCENTAGE ? 0 : 3;
}